import { ref } from 'vue'
import type { Socket } from 'socket.io-client'

export interface SocketConnectionConfig {
  serverUrl: string
  authToken: string
  isGokollab: boolean
  userV2?: boolean
  path?: string
  gokollabContactId?: string
  channel?: string
  source?: string
  version?: string
  visitorId?: string
}

interface QueuedMessage {
  event: string
  payload: unknown
  dedupeKey?: string
}

const socketRef = ref<Socket | null>(null)
const isConnected = ref(false)
const messageQueue: QueuedMessage[] = []

let activeConfigKey: string | null = null

// Named event handlers for proper cleanup
let handleConnect: (() => void) | null = null
let handleConnectionState: (() => void) | null = null
let handleDisconnect: (() => void) | null = null
let handleConnectError: ((error: Error) => void) | null = null
let handleReconnectAttempt: ((attempt: number) => void) | null = null

const buildConfigKey = (config: SocketConnectionConfig) => {
  return JSON.stringify({
    serverUrl: config.serverUrl,
    authToken: config.authToken,
    isGokollab: config.isGokollab,
    userV2: config.userV2,
    path: config.path,
    gokollabContactId: config.gokollabContactId,
    channel: config.channel,
    source: config.source,
    version: config.version,
    visitorId: config.visitorId,
  })
}

const getSocketPath = (config: SocketConnectionConfig) => {
  return config.path || '/sockets-communities/socket.io'
}

const enqueueMessage = (message: QueuedMessage) => {
  if (message.dedupeKey) {
    const exists = messageQueue.some(queued => queued.dedupeKey === message.dedupeKey)
    if (exists) {
      return
    }
  }

  messageQueue.push(message)
}

const flushMessageQueue = () => {
  if (!socketRef.value?.connected || messageQueue.length === 0) {
    return
  }

  const queuedMessages = messageQueue.splice(0, messageQueue.length)
  queuedMessages.forEach(({ event, payload }) => {
    socketRef.value?.emit(event, payload)
  })
}

class SocketService {
  async connect(config: SocketConnectionConfig): Promise<Socket | null> {
    if (!process.client) {
      return null
    }

    if (!config.serverUrl || !config.authToken) {
      console.warn('[Sockets] Missing server URL or auth token')
      return null
    }

    const configKey = buildConfigKey(config)
    if (socketRef.value && activeConfigKey === configKey) {
      return socketRef.value
    }

    if (socketRef.value) {
      // Reuse disconnect() to ensure cleanup stays consistent in one place.
      this.disconnect()
    }

    const { io } = await import('socket.io-client')
    const authHeader = `Bearer ${config.authToken}`
    const socketPath = getSocketPath(config)
    const shouldSendGokollab = config.isGokollab === true
    const shouldSendUserV2 = config.userV2 ?? !shouldSendGokollab

    const query: Record<string, string | boolean> = {
      authorization: authHeader,
      channel: config.channel || 'APP',
      source: config.source || 'PORTAL_USER',
      path: socketPath,
      version: config.version || '2021-07-28',
      visitorId: config.visitorId || '123',
    }

    if (shouldSendGokollab) {
      query.isGokollab = true
      if (config.gokollabContactId) {
        query.gokollabContactId = config.gokollabContactId
      }
    } else if (shouldSendUserV2) {
      query.userV2 = true
    }

    const socket = io(config.serverUrl, {
      path: socketPath,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query,
    })

    // Define named handlers for proper cleanup
    handleConnect = function onConnect() {
      isConnected.value = true
      flushMessageQueue()
    }

    handleConnectionState = function onConnectionState() {
      isConnected.value = true
      flushMessageQueue()
    }

    handleDisconnect = function onDisconnect() {
      isConnected.value = false
    }

    handleConnectError = function onConnectError(error: Error) {
      console.error('[Sockets] Connection error:', error)
    }

    handleReconnectAttempt = function onReconnectAttempt(attempt: number) {
      console.info('[Sockets] Reconnection attempt:', attempt)
    }

    // Register event handlers
    // Cleanup is handled in disconnect() method - handlers are stored in module-level variables.
    socket.on('connect', handleConnect)
    socket.on('connectionState', handleConnectionState)
    socket.on('disconnect', handleDisconnect)
    socket.on('connect_error', handleConnectError)
    socket.io.on('reconnect_attempt', handleReconnectAttempt)

    socketRef.value = socket
    activeConfigKey = configKey

    return socket
  }

  disconnect() {
    if (!socketRef.value) {
      return
    }

    const socket = socketRef.value

    // Remove specific event handlers before disconnecting
    if (handleConnect) {
      socket.off('connect', handleConnect)
      handleConnect = null
    }
    if (handleConnectionState) {
      socket.off('connectionState', handleConnectionState)
      handleConnectionState = null
    }
    if (handleDisconnect) {
      socket.off('disconnect', handleDisconnect)
      handleDisconnect = null
    }
    if (handleConnectError) {
      socket.off('connect_error', handleConnectError)
      handleConnectError = null
    }
    if (handleReconnectAttempt) {
      socket.io.off('reconnect_attempt', handleReconnectAttempt)
      handleReconnectAttempt = null
    }

    socket.disconnect()
    socketRef.value = null
    activeConfigKey = null
    isConnected.value = false
  }

  on<T = unknown>(event: string, handler: (payload: T) => void) {
    socketRef.value?.on(event, handler)
  }

  off<T = unknown>(event: string, handler?: (payload: T) => void) {
    if (handler) {
      socketRef.value?.off(event, handler)
      return
    }

    socketRef.value?.removeAllListeners(event)
  }

  emit(event: string, payload: unknown, options?: { queueIfDisconnected?: boolean; dedupeKey?: string }) {
    if (socketRef.value?.connected) {
      socketRef.value.emit(event, payload)
      return
    }

    if (options?.queueIfDisconnected) {
      enqueueMessage({ event, payload, dedupeKey: options.dedupeKey })
      return
    }

    console.warn('[Sockets] Emit skipped; socket not connected')
  }

  get socket() {
    return socketRef
  }

  get connected() {
    return isConnected
  }
}

export const socketService = new SocketService()
